import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminNav from '../components/AdminNav';
import LoadingSpinner from '../components/LoadingSpinner';
import { toast } from 'react-hot-toast';
import { useApi } from '../contexts/ApiContext';

const AdminBlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { api } = useApi();

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  // Helper function to detect YouTube URLs
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    const youtubeRegex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/;
    return youtubeRegex.test(url);
  };

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image_url: '',
    author_name: 'BandCam Team',
    author_email: '',
    status: 'draft',
    published_date: '',
    meta_description: '',
    meta_keywords: '',
    featured: false
  });

  const fetchPost = useCallback(async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('bandcam_admin_token');
      if (adminToken) {
        api.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      }
      
      const response = await api.get(`/admin/blog/${id}`);
      const data = response.data;
      
      // Format date for input field
      const publishedDate = data.published_date 
        ? new Date(data.published_date).toISOString().slice(0, 16)
        : '';

      setFormData({
        title: data.title || '',
        slug: data.slug || '',
        excerpt: data.excerpt || '',
        content: data.content || '',
        featured_image_url: data.featured_image_url || '',
        author_name: data.author_name || 'BandCam Team',
        author_email: data.author_email || '',
        status: data.status || 'draft',
        published_date: publishedDate,
        meta_description: data.meta_description || '',
        meta_keywords: data.meta_keywords || '',
        featured: data.featured || false
      });
    } catch (error) {
      console.error('Error fetching blog post:', error);
      toast.error('Failed to load blog post');
      navigate('/admin/blog');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  useEffect(() => {
    if (isEdit) {
      fetchPost();
    }
  }, [isEdit, fetchPost]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEdit && formData.title && !formData.slug) {
      const slug = formData.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  }, [formData.title, formData.slug, isEdit]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e, publish = false) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.slug.trim()) {
      toast.error('Slug is required');
      return;
    }
    // Content is now optional

    try {
      setSaving(true);
      
      // Convert published_date to ISO format if provided
      let formattedPublishedDate = null;
      if (formData.published_date) {
        // Convert datetime-local format to ISO string
        formattedPublishedDate = new Date(formData.published_date).toISOString();
      } else if (publish) {
        // If publishing and no date set, use current time
        formattedPublishedDate = new Date().toISOString();
      }
      
      const dataToSend = {
        ...formData,
        status: publish ? 'published' : formData.status,
        published_date: formattedPublishedDate,
        content: formData.content || '' // Ensure content is never null/undefined
      };

      // Set admin token for this request
      const adminToken = localStorage.getItem('bandcam_admin_token');
      if (adminToken) {
        api.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      }
      
      if (isEdit) {
        await api.put(`/admin/blog/${id}`, dataToSend);
      } else {
        await api.post('/admin/blog', dataToSend);
      }

      toast.success(publish 
        ? 'Blog post published successfully!'
        : isEdit 
          ? 'Blog post updated successfully!'
          : 'Blog post created successfully!'
      );
      
      navigate('/admin/blog');
    } catch (error) {
      console.error('Error saving blog post:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save blog post';
      const errorDetails = error.response?.data?.details || '';
      toast.error(errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNav />
        <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <LoadingSpinner />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Header */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {isEdit ? 'Edit Blog Post' : 'Create New Blog Post'}
            </h1>
            <p className="text-sm text-gray-600">
              {isEdit ? 'Update your blog post details below' : 'Fill in the details to create a new blog post'}
            </p>
          </div>

          {/* Main Content */}
          <div className="bg-white shadow-md rounded-lg p-6 space-y-6">
            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Title *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="Enter blog post title"
              />
            </div>

            {/* Slug */}
            <div>
              <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
                URL Slug *
              </label>
              <input
                type="text"
                id="slug"
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="url-friendly-slug"
              />
              <p className="mt-1 text-sm text-gray-500">
                URL: bandcam.io/blog/{formData.slug || 'your-slug'}
              </p>
            </div>

            {/* Excerpt */}
            <div>
              <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
                Excerpt
              </label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="Brief summary of the blog post (shown in listings)"
              />
            </div>

            {/* Content */}
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                Content (Optional)
              </label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                rows={15}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm text-gray-900 bg-white"
                placeholder="Enter blog post content (HTML supported)"
              />
              <p className="mt-1 text-sm text-gray-500">
                You can use HTML tags for formatting: &lt;p&gt;, &lt;h2&gt;, &lt;h3&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, etc.
              </p>
            </div>

            {/* Featured Image/Video */}
            <div>
              <label htmlFor="featured_image_url" className="block text-sm font-medium text-gray-700 mb-1">
                Featured Image URL or YouTube URL
              </label>
              <input
                type="url"
                id="featured_image_url"
                name="featured_image_url"
                value={formData.featured_image_url}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="https://example.com/image.jpg or https://youtube.com/watch?v=..."
              />
              {formData.featured_image_url && (
                <div className="mt-2">
                  {isYouTubeUrl(formData.featured_image_url) ? (
                    <div className="max-w-md">
                      <p className="text-sm text-gray-600 mb-2">YouTube Video Preview:</p>
                      <div className="aspect-video bg-gray-100 border rounded-lg flex items-center justify-center">
                        <span className="text-gray-500">YouTube video will be embedded</span>
                      </div>
                    </div>
                  ) : (
                    <img 
                      src={formData.featured_image_url} 
                      alt="Preview" 
                      className="max-w-md rounded-lg shadow"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="bg-white shadow-md rounded-lg p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Metadata & SEO</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Author Name */}
              <div>
                <label htmlFor="author_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Author Name
                </label>
                <input
                  type="text"
                  id="author_name"
                  name="author_name"
                  value={formData.author_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  placeholder="Author name"
                />
              </div>

              {/* Author Email */}
              <div>
                <label htmlFor="author_email" className="block text-sm font-medium text-gray-700 mb-1">
                  Author Email
                </label>
                <input
                  type="email"
                  id="author_email"
                  name="author_email"
                  value={formData.author_email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                  placeholder="author@bandcam.io"
                />
              </div>
            </div>

            {/* Meta Description */}
            <div>
              <label htmlFor="meta_description" className="block text-sm font-medium text-gray-700 mb-1">
                Meta Description
              </label>
              <textarea
                id="meta_description"
                name="meta_description"
                value={formData.meta_description}
                onChange={handleChange}
                maxLength={160}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="SEO meta description (max 160 characters)"
              />
              <p className="mt-1 text-sm text-gray-500">
                {(formData.meta_description || '').length}/160 characters
              </p>
            </div>

            {/* Meta Keywords */}
            <div>
              <label htmlFor="meta_keywords" className="block text-sm font-medium text-gray-700 mb-1">
                Meta Keywords
              </label>
              <input
                type="text"
                id="meta_keywords"
                name="meta_keywords"
                value={formData.meta_keywords}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
                placeholder="band, performance, video, recording"
              />
              <p className="mt-1 text-sm text-gray-500">
                Comma-separated keywords for SEO
              </p>
            </div>
          </div>

          {/* Publishing Options */}
          <div className="bg-white shadow-md rounded-lg p-6 space-y-6">
            <h2 className="text-lg font-semibold text-gray-900">Publishing Options</h2>

            {/* Status */}
            <div>
              <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            {/* Published Date */}
            <div>
              <label htmlFor="published_date" className="block text-sm font-medium text-gray-700 mb-1">
                Published Date
              </label>
              <input
                type="datetime-local"
                id="published_date"
                name="published_date"
                value={formData.published_date}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-900 bg-white"
              />
              <p className="mt-1 text-sm text-gray-500">
                Leave empty to publish immediately
              </p>
            </div>

            {/* Featured */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="featured"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                Feature this post on the homepage
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-white shadow-md rounded-lg p-6">
            <div className="flex flex-wrap gap-4">
              <button
                type="submit"
                disabled={saving}
                className="bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? 'Saving...' : isEdit ? 'Update Draft' : 'Save as Draft'}
              </button>

              {formData.status !== 'published' && (
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={saving}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {saving ? 'Publishing...' : 'Publish Now'}
                </button>
              )}

              <button
                type="button"
                onClick={() => navigate('/admin/blog')}
                disabled={saving}
                className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </form>
        </div>
      </main>
    </div>
  );
};

export default AdminBlogEditor;

